import { Injectable } from '@angular/core';
import { Block, BlockType, TemplateSlot } from '../interfaces';
import { CompiledPath, PathResolver, Session } from '@customer-apps/flexi-paths';

/**
 * Normalized metadata describing a relation expressed as:
 * owner.<relationProperty>[relationIndex] -> entity collection resolved via ID(...) path actions.
 */
type RelationConsistencyContext = {
    relationProperty: string;
    relationIndex: number;
    ownerPath: CompiledPath;
    ownerCollectionPath: CompiledPath;
    entityCollectionPath: CompiledPath;
};

/**
 * Highest index currently reachable for a specific ID-based relation context.
 * Used to detect when the flow shrinks and stale relation entries should be removed.
 */
type RelationIndexLimit = {
    context: RelationConsistencyContext;
    maxIndex: number;
};

/**
 * Highest index currently reachable for a plain indexed array path (without ID actions).
 * Example: rooms.{selectedRoomId}.heatSources.[n].type
 */
type IndexedArrayLimit = {
    arrayPath: CompiledPath;
    maxIndex: number;
};

/**
 * Snapshot of all cleanup-relevant index limits for the current question path.
 * We compare two snapshots (before/after path recalculation) to remove data that
 * is no longer represented by active blocks.
 */
export type QuestionFlowStateCleanupSnapshot = {
    relationLimits: Map<string, RelationIndexLimit>;
    indexedArrayLimits: Map<string, IndexedArrayLimit>;
};

@Injectable({ providedIn: 'root' })
export class QuestionFlowStateCleanupService {
    /**
     * Builds a cleanup snapshot for the currently active blocks.
     *
     * Why: path recalculation can hide/remove blocks, so we need a compact
     * representation of "what indexes are still valid" to safely trim state.
     *
     * @param isSlotActive Optional predicate — when provided, only slots for which
     * it returns `true` are included. This allows excluding slots that became
     * hidden or inactive (e.g. controlled by a checkbox) from the snapshot.
     */
    createSnapshot(blocks: Block[], isSlotActive?: (slot: TemplateSlot) => boolean): QuestionFlowStateCleanupSnapshot {
        return {
            relationLimits: this.collectRelationIndexLimits(blocks, isSlotActive),
            indexedArrayLimits: this.collectIndexedArrayLimits(blocks, isSlotActive)
        };
    }

    trimStateOnPathReduction(
        previousSnapshot: QuestionFlowStateCleanupSnapshot,
        currentSnapshot: QuestionFlowStateCleanupSnapshot,
        state: Record<string, any>,
        pathResolver: PathResolver,
        session: Session | undefined
    ): boolean {
        // First trim plain indexed arrays, then ID-based relation collections.
        // This order keeps both cleanup channels deterministic and independent.
        const indexedArraysTrimmed = this.trimIndexedArrays(
            previousSnapshot.indexedArrayLimits,
            currentSnapshot.indexedArrayLimits,
            state,
            pathResolver,
            session
        );
        const relationCollectionsTrimmed = this.trimRelationCollections(
            previousSnapshot.relationLimits,
            currentSnapshot.relationLimits,
            state,
            pathResolver,
            session
        );

        return indexedArraysTrimmed || relationCollectionsTrimmed;
    }

    /**
     * Ensures a single ID-based relation slot points to a valid entity.
     *
     * How:
     * 1) Parse relation context from compiled path tokens.
     * 2) If current reference exists, normalize missing entity.id field.
     * 3) If reference is empty, attach the last currently unlinked entity.
     *
     * Why: in dynamic flows, users can create entities and references in separate
     * steps. This keeps owner.<relationIds> and entity collection consistent.
     */
    ensureRelationReferenceConsistency(
        slot: TemplateSlot,
        state: Record<string, any>,
        pathResolver: PathResolver,
        session: Session | undefined
    ): boolean {
        const context = this.extractRelationConsistencyContext(slot);
        if (!context) {
            return false;
        }

        const ownerEntity = pathResolver.getValue(context.ownerPath, state, session) as Record<string, any> | undefined;
        const ownerCollection = pathResolver.getValue(context.ownerCollectionPath, state, session) as Record<string, any> | undefined;
        const entityCollection = pathResolver.getValue(context.entityCollectionPath, state, session) as Record<string, any> | undefined;

        if (!ownerEntity || !ownerCollection || !entityCollection) {
            return false;
        }

        ownerEntity[context.relationProperty] ??= [];
        if (!Array.isArray(ownerEntity[context.relationProperty])) {
            return false;
        }

        const currentRefId = ownerEntity[context.relationProperty][context.relationIndex];
        if (currentRefId) {
            if (entityCollection[currentRefId] && !entityCollection[currentRefId].id) {
                // Normalize entity shape if relation already points to an object without id.
                entityCollection[currentRefId].id = currentRefId;
                return true;
            }
            return false;
        }

        const linkedIds = this.getLinkedRelationIds(ownerCollection, context.relationProperty);
        // Pick the last unlinked entity as a deterministic candidate for this slot.
        const unlinkedEntityIds = Object.keys(entityCollection).filter(entityId => !linkedIds.has(entityId));
        const candidateEntityId = unlinkedEntityIds[unlinkedEntityIds.length - 1];
        if (!candidateEntityId) {
            return false;
        }

        ownerEntity[context.relationProperty][context.relationIndex] = candidateEntityId;
        if (!entityCollection[candidateEntityId].id) {
            entityCollection[candidateEntityId].id = candidateEntityId;
        }

        return true;
    }

    /**
     * Scans active question slots and records max index per ID-based relation context.
     */
    private collectRelationIndexLimits(blocks: Block[], isSlotActive?: (slot: TemplateSlot) => boolean): Map<string, RelationIndexLimit> {
        const limits = new Map<string, RelationIndexLimit>();

        blocks
            .filter(block => block.blockType === BlockType.QuestionBlock)
            .flatMap(block => block.template?.slots || [])
            .filter(slot => !isSlotActive || isSlotActive(slot))
            .forEach(slot => {
                const context = this.extractRelationConsistencyContext(slot);
                if (!context) {
                    return;
                }

                const key = this.getRelationContextKey(context);
                const currentLimit = limits.get(key);
                if (!currentLimit || context.relationIndex > currentLimit.maxIndex) {
                    // Keep only the highest visible relation index for this context.
                    limits.set(key, { context, maxIndex: context.relationIndex });
                }
            });

        return limits;
    }

    private trimRelationCollections(
        previousRelationLimits: Map<string, RelationIndexLimit>,
        currentRelationLimits: Map<string, RelationIndexLimit>,
        state: Record<string, any>,
        pathResolver: PathResolver,
        session: Session | undefined
    ): boolean {
        let hasChanges = false;
        const relationContextsByEntityCollection = this.getRelationContextsByEntityCollection(
            previousRelationLimits,
            currentRelationLimits
        );

        previousRelationLimits.forEach((previousLimit, key) => {
            const currentLimit = currentRelationLimits.get(key);
            // -1 means that this relation context disappeared entirely from active path.
            const desiredMaxIndex = currentLimit ? currentLimit.maxIndex : -1;
            if (desiredMaxIndex >= previousLimit.maxIndex) {
                return;
            }

            const context = currentLimit?.context ?? previousLimit.context;
            const ownerEntity = pathResolver.getValue(context.ownerPath, state, session) as Record<string, any> | undefined;
            const ownerCollection = pathResolver.getValue(context.ownerCollectionPath, state, session) as Record<string, any> | undefined;
            const entityCollection = pathResolver.getValue(context.entityCollectionPath, state, session) as Record<string, any> | undefined;

            if (!ownerCollection || !entityCollection) {
                return;
            }

            let relationWasTrimmed = false;
            if (ownerEntity) {
                const relationValue = ownerEntity[context.relationProperty];
                if (Array.isArray(relationValue)) {
                    const trimFromIndex = Math.max(desiredMaxIndex + 1, 0);
                    if (relationValue.length > trimFromIndex) {
                        // Remove stale relation ids from the tail and keep owner relation arrays in sync.
                        relationValue.splice(trimFromIndex);
                        relationWasTrimmed = true;
                    }

                    this.removeEmptyRelationArtifacts(context, ownerEntity, entityCollection, state, pathResolver, session);
                }
            }

            // Generic orphan pruning for the whole target collection.
            // Important for cascades like: room.wallIds -> walls -> wall.windowIds -> windows.
            const entityCollectionKey = this.getPathTokensKey(context.entityCollectionPath);
            const relatedContexts = relationContextsByEntityCollection.get(entityCollectionKey) ?? [context];
            const globallyLinkedEntityIds = this.getLinkedEntityIdsForEntityCollection(relatedContexts, state, pathResolver, session);
            const orphanEntityIds = Object.keys(entityCollection).filter(entityId => !globallyLinkedEntityIds.has(entityId));
            if (orphanEntityIds.length) {
                orphanEntityIds.forEach(entityId => {
                    delete entityCollection[entityId];
                });
                relationWasTrimmed = true;
            }

            if (relationWasTrimmed) {
                hasChanges = true;
            }
        });

        return hasChanges;
    }

    private getRelationContextsByEntityCollection(
        previousRelationLimits: Map<string, RelationIndexLimit>,
        currentRelationLimits: Map<string, RelationIndexLimit>
    ): Map<string, RelationConsistencyContext[]> {
        const contextsByEntityCollection = new Map<string, RelationConsistencyContext[]>();

        const allContexts = [
            ...Array.from(previousRelationLimits.values()).map(limit => limit.context),
            ...Array.from(currentRelationLimits.values()).map(limit => limit.context)
        ];

        allContexts.forEach(context => {
            const entityCollectionKey = this.getPathTokensKey(context.entityCollectionPath);
            const contexts = contextsByEntityCollection.get(entityCollectionKey) ?? [];
            const contextKey = this.getRelationContextKey(context);

            if (!contexts.some(existingContext => this.getRelationContextKey(existingContext) === contextKey)) {
                contexts.push(context);
            }

            contextsByEntityCollection.set(entityCollectionKey, contexts);
        });

        return contextsByEntityCollection;
    }

    private getLinkedEntityIdsForEntityCollection(
        contexts: RelationConsistencyContext[],
        state: Record<string, any>,
        pathResolver: PathResolver,
        session: Session | undefined
    ): Set<string> {
        const linkedIds = new Set<string>();

        contexts.forEach(context => {
            const ownerCollection = pathResolver.getValue(context.ownerCollectionPath, state, session) as Record<string, any> | undefined;
            if (!ownerCollection) {
                return;
            }

            this.getLinkedRelationIds(ownerCollection, context.relationProperty).forEach(id => {
                linkedIds.add(id);
            });
        });

        return linkedIds;
    }

    /**
     * Scans active slots and records max index per plain indexed array context
     * (paths that do not use ACTION/ID token resolution).
     */
    private collectIndexedArrayLimits(blocks: Block[], isSlotActive?: (slot: TemplateSlot) => boolean): Map<string, IndexedArrayLimit> {
        const limits = new Map<string, IndexedArrayLimit>();

        blocks
            .filter(block => block.blockType === BlockType.QuestionBlock)
            .flatMap(block => block.template?.slots || [])
            .filter(slot => !isSlotActive || isSlotActive(slot))
            .forEach(slot => {
                const compiledPath = slot.compiledOutputParameter ?? slot.compiledOutputPath;
                const context = this.extractIndexedArrayContext(compiledPath);
                if (!context) {
                    return;
                }

                const currentLimit = limits.get(context.key);
                if (!currentLimit || context.arrayIndex > currentLimit.maxIndex) {
                    // Keep only the highest visible index per normalized array path.
                    limits.set(context.key, {
                        arrayPath: context.arrayPath,
                        maxIndex: context.arrayIndex
                    });
                }
            });

        return limits;
    }

    private extractIndexedArrayContext(
        compiledPath: CompiledPath | undefined
    ): { key: string; arrayPath: CompiledPath; arrayIndex: number } | undefined {
        const tokens = (compiledPath as any)?.tokens as any[] | undefined;
        // This extractor is intentionally for simple indexed arrays only.
        // ID-based relations are handled by extractRelationConsistencyContext().
        if (!tokens?.length || tokens.some(token => token.kind === 'ACTION')) {
            return undefined;
        }

        let indexTokenPosition = -1;
        // Find the last array index that is part of an object/array navigation chain.
        for (let idx = tokens.length - 1; idx >= 0; idx--) {
            const token = tokens[idx];
            if (token.kind !== 'INDEX') {
                continue;
            }

            const previousToken = tokens[idx - 1];
            if (previousToken?.kind === 'PROP' || previousToken?.kind === 'DYNAMIC') {
                indexTokenPosition = idx;
                break;
            }
        }

        if (indexTokenPosition <= 0) {
            return undefined;
        }

        const indexToken = tokens[indexTokenPosition];
        if (typeof indexToken.index !== 'number') {
            return undefined;
        }

        const arrayPathTokens = tokens.slice(0, indexTokenPosition);
        // Stable key for grouping equivalent array contexts across slots/blocks.
        const key = JSON.stringify(arrayPathTokens);

        return {
            key,
            arrayPath: {
                originalPath: '',
                tokens: arrayPathTokens,
                dynamicSlots: (compiledPath as any).dynamicSlots ?? []
            } as CompiledPath,
            arrayIndex: indexToken.index
        };
    }

    private trimIndexedArrays(
        previousIndexedArrayLimits: Map<string, IndexedArrayLimit>,
        currentIndexedArrayLimits: Map<string, IndexedArrayLimit>,
        state: Record<string, any>,
        pathResolver: PathResolver,
        session: Session | undefined
    ): boolean {
        let hasChanges = false;

        previousIndexedArrayLimits.forEach((previousLimit, key) => {
            const currentLimit = currentIndexedArrayLimits.get(key);
            // If context disappears, desired index is -1 (remove whole array content).
            const desiredMaxIndex = currentLimit ? currentLimit.maxIndex : -1;
            if (desiredMaxIndex >= previousLimit.maxIndex) {
                return;
            }

            const arrayValue = pathResolver.getValue(previousLimit.arrayPath, state, session) as unknown;
            if (!Array.isArray(arrayValue)) {
                return;
            }

            const trimFromIndex = Math.max(desiredMaxIndex + 1, 0);
            if (arrayValue.length <= trimFromIndex) {
                return;
            }

            // Keep only active prefix of the array.
            arrayValue.splice(trimFromIndex);

            hasChanges = true;
        });

        return hasChanges;
    }

    private removeEmptyRelationArtifacts(
        context: RelationConsistencyContext,
        ownerEntity: Record<string, any>,
        entityCollection: Record<string, any>,
        state: Record<string, any>,
        pathResolver: PathResolver,
        session: Session | undefined
    ): void {
        // Intentionally keep empty relation artifacts in state:
        // - owner relation arrays should stay as []
        // - entity collections should stay as {}
        // This preserves explicit empty structures required by downstream consumers.
        void context;
        void ownerEntity;
        void entityCollection;
        void state;
        void pathResolver;
        void session;
    }

    /**
     * Extracts relation context from nested ACTION(ID) tokens.
     *
     * Expected shape:
     * - Outer ACTION(ID): resolves entity collection item.
     * - Inner ACTION(ID): resolves owner entity via relation array slot.
     * - Tail tokens in inner target: PROP(relationProperty), INDEX(relationIndex).
     */
    private extractRelationConsistencyContext(slot: TemplateSlot): RelationConsistencyContext | undefined {
        const compiledPath = slot.compiledOutputParameter ?? slot.compiledOutputPath;
        const tokens = (compiledPath as any)?.tokens as any[] | undefined;
        if (!tokens?.length) {
            return undefined;
        }

        const outerActionIndex = tokens.findIndex(token => token.kind === 'ACTION' && token.actionType === 'ID');
        if (outerActionIndex < 0) {
            return undefined;
        }

        const outerAction = tokens[outerActionIndex];
        const outerTargetTokens = outerAction?.targetTemplate as any[] | undefined;
        if (!outerTargetTokens?.length) {
            return undefined;
        }

        const innerActionIndex = outerTargetTokens.findIndex(token => token.kind === 'ACTION' && token.actionType === 'ID');
        if (innerActionIndex < 0) {
            return undefined;
        }

        const innerAction = outerTargetTokens[innerActionIndex];
        const innerTargetTokens = innerAction?.targetTemplate as any[] | undefined;
        if (!innerTargetTokens || innerTargetTokens.length < 2) {
            return undefined;
        }

        const relationPropertyToken = innerTargetTokens[innerTargetTokens.length - 2];
        const relationIndexToken = innerTargetTokens[innerTargetTokens.length - 1];
        if (relationPropertyToken?.kind !== 'PROP' || relationIndexToken?.kind !== 'INDEX') {
            return undefined;
        }

        const relationProperty = relationPropertyToken.name as string;
        const relationIndex = relationIndexToken.index as number;
        if (!relationProperty || typeof relationIndex !== 'number') {
            return undefined;
        }

        const ownerPathTokens = innerTargetTokens.slice(0, -2);
        // Owner collection is everything before the token that resolves a single owner entity
        // (DYNAMIC key access or ACTION(ID) selection).
        const ownerCollectionBoundaryIndex = ownerPathTokens.findIndex(
            token => token.kind === 'DYNAMIC' || (token.kind === 'ACTION' && token.actionType === 'ID')
        );
        if (ownerCollectionBoundaryIndex <= 0) {
            return undefined;
        }

        const ownerCollectionPathTokens = ownerPathTokens.slice(0, ownerCollectionBoundaryIndex);
        const entityCollectionPathTokens = tokens.slice(0, outerActionIndex);

        return {
            relationProperty,
            relationIndex,
            ownerPath: {
                originalPath: '',
                tokens: ownerPathTokens,
                dynamicSlots: (compiledPath as any).dynamicSlots ?? []
            } as CompiledPath,
            ownerCollectionPath: {
                originalPath: '',
                tokens: ownerCollectionPathTokens,
                dynamicSlots: (compiledPath as any).dynamicSlots ?? []
            } as CompiledPath,
            entityCollectionPath: {
                originalPath: '',
                tokens: entityCollectionPathTokens,
                dynamicSlots: (compiledPath as any).dynamicSlots ?? []
            } as CompiledPath
        };
    }

    /**
     * Returns all ids currently referenced by ownerCollection[relationProperty].
     * Used to detect orphan entities after relation trimming.
     */
    private getLinkedRelationIds(ownerCollection: Record<string, any>, relationProperty: string): Set<string> {
        const linkedIds = Object.values(ownerCollection)
            .flatMap(owner => {
                const relationValue = owner?.[relationProperty];
                return Array.isArray(relationValue) ? relationValue : [];
            })
            .filter((id: string) => !!id);

        return new Set<string>(linkedIds);
    }

    /**
     * Builds a stable grouping key for relation contexts.
     * Two slots belong to the same relation group if they resolve through the same
     * owner path + entity collection path and point to the same relation property.
     */
    private getRelationContextKey(context: RelationConsistencyContext): string {
        return JSON.stringify({
            relationProperty: context.relationProperty,
            ownerPathTokens: (context.ownerPath as any).tokens,
            entityCollectionPathTokens: (context.entityCollectionPath as any).tokens
        });
    }

    private getPathTokensKey(path: CompiledPath): string {
        return JSON.stringify((path as any)?.tokens ?? []);
    }
}
